import { useCallback } from 'react';

const dateFormatServer = 'YYYY-MM-DD';
const dateFnsFormatServer = 'yyyy-MM-dd';
const dateFormatServerWithTime = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
const dateFormatDisplayShort = 'M/d/yy';
const dateFormatDisplayLong = 'EEEE: LLLL, do';
const dateFormatDisplayDayShort = 'EEEEEE';
const dateFormatControl = 'yyyy-MM-DDTHH:mm';
const dateFormatDateControl = 'yyyy-MM-DD';
const dateFormatTimeControl = 'HH:mm';
const timeFormatDisplay = 'h:mm a';

var datefns = require('date-fns');

/*
Date format inventory

From the server (json)
shortDateString Workday.workDate, Card.startDate, .endDate: 2021-01-10
dateString Entry.endDate: 2020-12-17T00:00:00.000Z with time always "blank"
timeString Entry.endTime: 1900-01-01T13:04:00.000Z with date always "blank"

In components
Date picker: native date
Time picker: native date
On workday: 


*/

const dateUtil = {
  //converts a string in YYYY-MM-DDTHH:mm:ss.SSS[Z] or YYYY-MM-DD to a native date
  stringToDate: (dateString) => {
    if (!dateString) return null;
    if (dateString.indexOf('T') != -1) {
      const s1 = dateString.split('T');
      const s2 = s1[0].split('-');
      return new Date(parseInt(s2[0]), parseInt(s2[1]) - 1, parseInt(s2[2]));
    } else {
      const s2 = dateString.split('-');
      return new Date(parseInt(s2[0]), parseInt(s2[1]) - 1, parseInt(s2[2]));
    }
  },

  stringToTime: (timeString) => {
    if (!timeString) return null;
    if (timeString.indexOf('T') != -1) {
      const s1 = timeString.split('T');
      const s2 = s1[1].split(':');
      return new Date(1900, 1, 1, parseInt(s2[0]), parseInt(s2[1]));
    } else {
      const s3 = timeString.split(':');
      return new Date(1900, 1, 1, parseInt(s3[0]), parseInt(s3[1]));
    }
  },

  toDateString: (nativeDate) => {
    if (!nativeDate) return null;
    return datefns.format(nativeDate, "yyyy-MM-dd'T'00:00:00.000'Z'");
  },
  toShortDateString: (nativeDate) => {
    if (!nativeDate) return null;
    return datefns.format(nativeDate, 'yyyy-MM-dd');
  },
  stringToShortDateString: (dateString) => {
    if (!dateString) return null;
    if (dateString.indexOf('T') == -1) return dateString;
    return dateString.split('T')[0];
  },
  toTimeString: (nativeTime) => {
    if (!nativeTime) return null;
    return datefns.format(nativeTime, "1900-01-01'T'HH:mm:00.000'Z'");
  },
  toDateTimeString: (nativeTime) => {
    if (!nativeTime) return null;
    return datefns.format(nativeTime, "yyyy-MM-dd'T'HH:mm:00.000'Z'");
  },

  nowDateString: () => {
    return datefns.format(new Date(), "yyyy-MM-dd'T'00:00:00.000'Z'");
  },
  nowShortDateString: () => {
    return datefns.format(new Date(), 'yyyy-MM-dd');
  },
  nowTimeString: () => {
    return datefns.format(new Date(), "1900-01-01'T'HH:mm:00.000'Z'");
  },

  calcHours: (startDateString, startTimeString, endDateString, endTimeString) => {
    if (!startDateString || !startTimeString || !endDateString || !endTimeString)
      return 0.0;
    var st = dateUtil.combine(
      dateUtil.stringToDate(startDateString),
      dateUtil.stringToTime(startTimeString)
    );
    var et = dateUtil.combine(
      dateUtil.stringToDate(endDateString),
      dateUtil.stringToTime(endTimeString)
    );
    if (!st || !et) return 0.0;
    return ((et.valueOf() - st.valueOf()) / (1000 * 60 * 60)).toFixed(2);
  },
  combine: (nativeDate, nativeTime) => {
    if (!nativeDate) return null;
    if (!nativeTime) return nativeDate;
    const d = datefns.add(nativeDate, {
      hours: nativeTime.getHours(),
      minutes: nativeTime.getMinutes(),
    });
    return d;
  },
  isBeforeToday: (dateString) => {
    return dateUtil.stringToShortDateString(dateString) < dateUtil.nowShortDateString();
  },
  isToday: (dateString) => {
    return dateUtil.isSameDay(dateString, dateUtil.nowShortDateString());
  },
  isAfterToday: (dateString) => {
    return dateUtil.isAfter(dateString, dateUtil.nowShortDateString());
  },
  isAfter: (dateString1, dateString2) => {
    if (!dateString1 || !dateString2) return false;
    return (
      dateUtil.stringToShortDateString(dateString1) >
      dateUtil.stringToShortDateString(dateString2)
    );
  },
  isBefore: (dateString1, dateString2) => {
    if (!dateString1 || !dateString2) return false;
    return (
      dateUtil.stringToShortDateString(dateString1) <
      dateUtil.stringToShortDateString(dateString2)
    );
  },
  isSameDay: (dateString1, dateString2) => {
    if (!dateString1 || !dateString2) return false;
    return (
      dateUtil.stringToShortDateString(dateString1) ==
      dateUtil.stringToShortDateString(dateString2)
    );
  },
  stringToShortDateFormat: (shortDateString) => {
    if (!shortDateString) return null;
    const date = datefns.parse(shortDateString, 'yyyy-MM-dd', new Date());
    return datefns.lightFormat(date, 'M/d/yy');
  },
  stringToLongDateFormat: (shortDateString) => {
    if (!shortDateString) return null;
    const date = datefns.parse(shortDateString, 'yyyy-MM-dd', new Date());
    return datefns.format(date, 'EEEE: LLLL do');
  },
  stringToDateFormat: (shortDateString, format) => {
    if (!shortDateString) return null;
    //const date = datefns.parse(shortDateString, 'yyyy-MM-dd', new Date());
    const date = dateUtil.stringToDate(shortDateString);
    return datefns.format(date, format);
  },
  stringToMediumDateFormat: (shortDateString) => {
    if (!shortDateString) return null;
    const date = datefns.parse(shortDateString, 'yyyy-MM-dd', new Date());
    return datefns.format(date, 'EEE: L/d');
  },
  stringToShortDayFormat: (dateString) => {
    if (!dateString) return null;
    const shortDateString = dateUtil.stringToShortDateString(dateString);
    const date = datefns.parse(shortDateString, 'yyyy-MM-dd', new Date());
    return datefns.format(date, 'EEEEEE');
  },
  stringToTimeFormat: (timeString, format) => {
    if (!timeString) return null;
    const date = dateUtil.stringToTime(timeString);
    return datefns.lightFormat(date, format);
  },
  isWeekend: (dateString) => {
    if (!dateString) return false;
    const shortDateString = dateUtil.stringToShortDateString(dateString);
    const s2 = shortDateString.split('-');
    const d = new Date(parseInt(s2[0]), parseInt(s2[1]) - 1, parseInt(s2[2]));
    return datefns.isWeekend(d);
  },
};

export default dateUtil;
